import { api } from "@/lib/api-client"

export type AdminDataSourcesResponse = {
  sources: Array<{
    id: string
    type: string
    name: string
    status: string
    items: number
    lastSync: string | null
    m365Group?: string | null
    alias?: string | null
    groupOk?: boolean | null
    appPermission?: string | null
    embeddings?: Record<string, unknown> | null
  }>
}

export type AdminAzureOverviewResponse = {
  region: string
  deployment: string
  rateLimits?: {
    rpm?: number
    tpm?: number
  }
  contentFilterHitPct: number
  health: string
  p95?: number
  errorPct?: number
  services: Array<{
    name: string
    status: string
  }>
}

export async function getDataSources(): Promise<AdminDataSourcesResponse> {
  const response = await api.get(`/admin/datasources`)
  return response.data || response
}

export type AdminEmbeddingModel = {
  id: string
  displayName: string
  enabled: boolean
  deployment?: string
  model_name?: string
  dimensions?: number
}

export async function getEmbeddingModels(): Promise<AdminEmbeddingModel[]> {
  const response = await api.get(`/admin/embeddings`)
  return response.data || response
}

export async function getAzureOverview(): Promise<AdminAzureOverviewResponse> {
  const response = await api.get(`/admin/azure/overview`)
  return response.data || response
}

export type AdminNavigatorLibrariesResponse = {
  libraries: Array<{
    id: string
    url: string
    m365Group: string | null
    alias: string | null
    groupOk: boolean | null
    appPermission: string | null
    embeddings: Record<string, unknown> | null
    verifiedAt: string | null
  }>
}

export async function getNavigatorLibraries(): Promise<AdminNavigatorLibrariesResponse> {
  const response = await api.get(`/admin/navigator/libraries`)
  return response.data || response
}

export type AdminPerformanceStatsResponse = {
  config: {
    project_cache_enabled: boolean
    session_cache_enabled: boolean
    index_probe_cache_enabled: boolean
    cosmos_pool_enabled: boolean
    performance_tracking_enabled: boolean
    trust_frontend_file_signal: boolean
  }
  ttls: {
    project_cache_ttl_seconds: number
    session_cache_ttl_seconds: number
    index_probe_cache_ttl_seconds: number
  }
  cosmos_pool: {
    pooled_clients: number
    total_requests: number
  }
  project_cache?: {
    hits: number
    misses: number
    hit_rate_pct: number
    size: number
  }
  session_cache?: {
    hits: number
    misses: number
    hit_rate_pct: number
    size: number
  }
  note?: string
}

export async function getPerformanceStats(): Promise<AdminPerformanceStatsResponse> {
  const response = await api.get("/performance/stats")
  return response.data || response
}

// System Health Types
export type SystemHealthResponse = {
  uptime: {
    start_time: string
    uptime_seconds: number
    uptime_formatted: string
  }
  resources: {
    memory_used_mb: number
    memory_total_mb: number
    memory_percent: number
    cpu_percent: number
  }
}

export async function getSystemHealth(): Promise<SystemHealthResponse> {
  const response = await api.get("/admin/system-health")
  return response.data || response
}

// Health Check Types
export type HealthCheckResponse = {
  status: "healthy" | "degraded" | "unhealthy"
  timestamp: string
  check_duration_ms: number
  services: Record<
    string,
    {
      status: "ok" | "error" | "skipped" | "degraded"
      latency_ms?: number
      error?: string
      details?: Record<string, unknown>
    }
  >
}

// The admin diagnostics endpoint: the public /health readiness view strips the
// timestamp, latency and per-service details this tab renders (#3322).
export async function getHealthCheck(): Promise<HealthCheckResponse> {
  const response = await api.get("/admin/health")
  return response.data || response
}

// My Groups (resolved from token claims via app token)
export type MyGroup = {
  oid: string
  displayName: string
  type: "group"
  email?: string
}

export async function getMyGroups(): Promise<MyGroup[]> {
  const response = await api.get("/graph/me/groups")
  const data = response.data || response
  return data.groups ?? []
}
